import type {
  Todo,
  CreateTodoRequest,
  UpdateTodoRequest,
  UpdateTodoResponse,
  TodoStatus,
  TodoPriority,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface TodoFilters {
  assignedTo?: string;
  status?: TodoStatus;
  priority?: TodoPriority;
  /** When true, completed ('done') tasks are included */
  includeDone?: boolean;
}

export async function fetchTodos(filters?: TodoFilters, signal?: AbortSignal): Promise<Todo[]> {
  const qs = new URLSearchParams();
  if (filters?.assignedTo) qs.set('assignedTo', filters.assignedTo);
  if (filters?.status) qs.set('status', filters.status);
  if (filters?.priority) qs.set('priority', filters.priority);
  if (filters?.includeDone) qs.set('includeDone', 'true');

  const query = qs.toString();
  const response = await fetch(`${API_BASE_URL}/todos${query ? `?${query}` : ''}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch to-do tasks: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export async function createTodo(
  payload: CreateTodoRequest,
  signal?: AbortSignal,
): Promise<Todo> {
  const response = await fetch(`${API_BASE_URL}/todos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to create to-do task: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

export async function updateTodo(
  id: string,
  payload: UpdateTodoRequest,
  signal?: AbortSignal,
): Promise<UpdateTodoResponse | undefined> {
  const response = await fetch(`${API_BASE_URL}/todos/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to update to-do task: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  // 204 No Content for plain updates; 200 with { billing } when completing a
  // task triggered a billing action/decision.
  if (response.status === 204) return undefined;
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

export async function deleteTodo(id: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/todos/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to delete to-do task: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
}
